import {useEffect, useState} from "react";
import {PhoneHQGlyph, PhoneHQLoader, PhoneHQButton} from 'ch3-ui-lib';
import {Bell, BellOff, Check, ChevronDown, ChevronUp, Info} from "lucide-react";
import {
    useConfirmEnsAlertMutation,
    useListMyEnsAlertsQuery,
} from "../../../store/features/ens/ensService";
import {TypeBadge} from "../components/TypeBadge";
import {LevelBadge} from "../components/LevelBadge";
import {ChannelBadge} from "../components/ChannelBadge";
import {formatDateTime} from "../utils";
import {ENS_NOTIFICATION_POLL_INTERVAL_MS} from "../Notifications/ensNotificationPolling";
import {browserNotificationService} from "../../../services/browserNotificationService";
import {StatusBadge} from "../components/StatusBadge";
import {EnsPagination} from "../components/EnsPagination";

const PAGE_SIZE = 20;
const FILTERS = ['All', 'Pending', 'Confirmed'] as const;
type AlertFilter = typeof FILTERS[number];

const compactDateTime = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

const formatCompactDateTime = (iso: string): string => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '—' : compactDateTime.format(date);
};

const MyAlerts = () => {
    const [filter, setFilter] = useState<AlertFilter>('All');
    const [page, setPage] = useState(0);
    const [expandedAlertId, setExpandedAlertId] = useState<string>();
    const {
        currentData: data,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useListMyEnsAlertsQuery({
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        recipientStatus: filter === 'All' ? undefined : filter === 'Pending' ? 'PENDING' : 'CONFIRMED',
    }, {
        pollingInterval: ENS_NOTIFICATION_POLL_INTERVAL_MS,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });
    const [confirmAlert, {isLoading: isConfirming}] = useConfirmEnsAlertMutation();
    const [notificationPermission, setNotificationPermission] = useState<
        NotificationPermission | 'unsupported'
    >(() => 'Notification' in window ? Notification.permission : 'unsupported');

    const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE));

    useEffect(() => {
        if (data && !isFetching && page >= totalPages) {
            setPage(totalPages - 1);
            setExpandedAlertId(undefined);
        }
    }, [data, isFetching, page, totalPages]);

    useEffect(() => {
        if (data && !isFetching && !data.data.some((alert) => alert.publicId === expandedAlertId)) {
            setExpandedAlertId(undefined);
        }
    }, [data, expandedAlertId, isFetching]);

    useEffect(() => {
        const handlePageShow = () => {
            void refetch();
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [refetch]);

    const enableDesktopNotifications = async () => {
        if (!('Notification' in window)) {
            setNotificationPermission('unsupported');
            return;
        }
        if (Notification.permission === 'default') {
            await browserNotificationService.requestPermission();
            setNotificationPermission(Notification.permission);
            return;
        }
        setNotificationPermission(Notification.permission);
    };

    const alerts = data?.data ?? [];
    const isInitialLoading = isLoading || (!data && isFetching);
    const totalCount = data?.totalCount ?? 0;

    const changePage = (nextPage: number) => {
        setPage(nextPage);
        setExpandedAlertId(undefined);
    };

    return (
        <section
            aria-labelledby="my-alerts-heading"
            className="mx-auto flex w-full min-w-0 max-w-[1100px] flex-col gap-4 px-4 pb-8 min-[961px]:px-6 [&_button:focus-visible]:outline [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-2 [&_button:focus-visible]:[outline-color:var(--indigo-500)]"
        >
            <div className="flex flex-wrap items-center gap-4 pb-4 pt-8 min-[961px]:pb-6 min-[961px]:pt-12">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-zinc-700 text-white">
                    <Bell size={18} aria-hidden="true"/>
                </div>
                <div className="min-w-0 flex-1 basis-40">
                    <h1 id="my-alerts-heading" className="text-2xl font-medium leading-8 text-zinc-900">
                        Notifications
                    </h1>
                    <p className="mt-1 text-sm leading-5 text-zinc-500">
                        Review your alerts and confirm those awaiting your response.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Filter by your status" className="flex flex-wrap gap-2">
                    {FILTERS.map((option) => (
                        <PhoneHQButton
                            key={option}
                            type="button"
                            variant="tertiary"
                            aria-pressed={filter === option}
                            className={`min-h-11 !rounded-xl !px-4 !text-sm shadow-none motion-reduce:transition-none min-[961px]:min-h-9 ${filter === option
                                ? '!bg-[var(--indigo-100)] !text-[var(--indigo-800)]'
                                : '!bg-zinc-100 !text-zinc-600 hover:!bg-zinc-200'}`}
                            onClick={() => {
                                setFilter(option);
                                changePage(0);
                            }}
                        >
                            {option}
                        </PhoneHQButton>
                    ))}
                </div>
                {notificationPermission === 'default' && (
                    <PhoneHQButton
                        phq-id="myAlerts-enableDesktopNotificationsBtn"
                        variant="secondary"
                        className="h-auto min-h-11 max-w-full whitespace-normal py-2 motion-reduce:transition-none min-[961px]:min-h-9"
                        onClick={() => void enableDesktopNotifications()}
                    >
                        <Bell size={16} className="shrink-0 !p-0" aria-hidden="true"/>
                        Enable desktop notifications
                    </PhoneHQButton>
                )}
                {notificationPermission === 'granted' && (
                    <span className="flex items-center gap-2 text-xs text-zinc-500">
                        <Check size={16} className="shrink-0 text-success-ink" aria-hidden="true"/>
                        Desktop notifications enabled
                    </span>
                )}
            </div>

            {notificationPermission === 'denied' && (
                <div className="flex items-start gap-2 rounded-xl bg-warning-tint px-3 py-2 text-xs leading-5 text-warning-ink">
                    <BellOff size={16} className="mt-0.5 shrink-0" aria-hidden="true"/>
                    <p>Desktop notifications are blocked. Enable them in your browser's site settings. In-page alerts will continue to work.</p>
                </div>
            )}
            {notificationPermission === 'unsupported' && (
                <div className="flex items-start gap-2 rounded-xl bg-zinc-100 px-3 py-2 text-xs leading-5 text-zinc-600">
                    <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true"/>
                    <p>This browser does not support desktop notifications. In-page alerts will continue to work.</p>
                </div>
            )}

            <div className="min-w-0 min-[961px]:rounded-2xl min-[961px]:bg-zinc-100 min-[961px]:p-1">
                <div className="min-w-0 min-[961px]:overflow-hidden min-[961px]:rounded-xl min-[961px]:border min-[961px]:border-solid min-[961px]:border-zinc-200 min-[961px]:bg-surface">
                    {isInitialLoading ? (
                        <div role="status" aria-label="Loading notifications" className="flex justify-center rounded-xl bg-zinc-50 py-16">
                            <PhoneHQLoader className="!h-auto"/>
                        </div>
                    ) : isError ? (
                        <div role="alert" className="flex flex-col items-center gap-3 rounded-xl bg-danger-tint px-4 py-10 text-center">
                            <PhoneHQGlyph name="error" size={20} className="text-danger-ink"/>
                            <div>
                                <p className="text-sm font-semibold text-danger-ink">Emergency alerts could not be loaded.</p>
                                <p className="mt-1 text-xs text-danger-ink">Check your connection and try again.</p>
                            </div>
                            <PhoneHQButton
                                phq-id="myAlerts-retryBtn"
                                variant="secondary"
                                className="min-h-11 motion-reduce:transition-none min-[961px]:min-h-9"
                                disabled={isFetching}
                                onClick={() => void refetch()}
                            >
                                {isFetching ? 'Retrying…' : 'Retry'}
                            </PhoneHQButton>
                        </div>
                    ) : alerts.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 rounded-xl bg-zinc-50 px-4 py-12 text-center">
                            <div className="flex size-12 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-500">
                                <Bell size={24} aria-hidden="true"/>
                            </div>
                            <p className="text-sm text-zinc-600">
                                {filter === 'All' ? 'You have no emergency alerts.' : `You have no ${filter.toLowerCase()} emergency alerts.`}
                            </p>
                        </div>
                    ) : (
                        <ul aria-label="Emergency alerts" className="flex min-w-0 flex-col gap-3 min-[961px]:gap-0">
                            {alerts.map((alert) => {
                                const canConfirm = alert.recipientStatus === 'PENDING'
                                    && (alert.alertStatus === 'PENDING' || alert.alertStatus === 'ESCALATING');
                                const isExpanded = expandedAlertId === alert.publicId;
                                const showClosedStatus = alert.recipientStatus === 'PENDING' && !canConfirm;
                                const detailsId = `my-alert-details-${alert.publicId}`;

                                return (
                                    <li
                                        key={alert.publicId}
                                        className={`relative min-w-0 overflow-hidden rounded-xl border border-solid border-zinc-200 bg-surface min-[961px]:rounded-none min-[961px]:border-x-0 min-[961px]:border-t-0 min-[961px]:last:border-b-0 ${canConfirm
                                            ? 'before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-[var(--indigo-500)]'
                                            : ''}`}
                                    >
                                        <div className="flex min-w-0 flex-col gap-3 p-4 min-[961px]:px-5">
                                            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                                                <div className="flex min-w-0 flex-1 basis-48 flex-wrap items-center gap-2">
                                                    <TypeBadge type={alert.type} size="md" className="!border-0"/>
                                                    <h2 className="min-w-0 break-words text-sm font-semibold leading-5 text-zinc-900 [overflow-wrap:anywhere]">
                                                        {alert.name}
                                                    </h2>
                                                </div>
                                                <time dateTime={alert.triggeredAt} title={formatDateTime(alert.triggeredAt)} className="text-xs leading-5 text-zinc-500">
                                                    <span className="sr-only">Triggered </span>{formatCompactDateTime(alert.triggeredAt)}
                                                </time>
                                            </div>
                                            {!isExpanded && (
                                                <p className="line-clamp-2 whitespace-pre-line break-words text-sm leading-5 text-zinc-600 [overflow-wrap:anywhere]">
                                                    {alert.message}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-500">
                                                    <span className="flex flex-wrap items-center gap-2">
                                                        Your status <StatusBadge status={alert.recipientStatus} size="md" className="!rounded-full !border-0"/>
                                                    </span>
                                                    {showClosedStatus && !isExpanded && (
                                                        <span className="flex flex-wrap items-center gap-2">
                                                            Overall alert status <StatusBadge status={alert.alertStatus} size="md" className="!rounded-full !border-0"/>
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <PhoneHQButton
                                                        phq-id={`myAlerts-detailsBtn-${alert.publicId}`}
                                                        variant="tertiary"
                                                        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${alert.name}`}
                                                        aria-expanded={isExpanded}
                                                        aria-controls={detailsId}
                                                        className="min-h-11 !bg-transparent shadow-none hover:!bg-zinc-100 motion-reduce:transition-none min-[961px]:min-h-9"
                                                        onClick={() => setExpandedAlertId(isExpanded ? undefined : alert.publicId)}
                                                    >
                                                        {isExpanded ? 'Hide details' : 'Details'}
                                                        {isExpanded
                                                            ? <ChevronUp size={16} className="!p-0" aria-hidden="true"/>
                                                            : <ChevronDown size={16} className="!p-0" aria-hidden="true"/>}
                                                    </PhoneHQButton>
                                                    {canConfirm && (
                                                        <PhoneHQButton
                                                            phq-id={`myAlerts-confirmBtn-${alert.publicId}`}
                                                            className="min-h-11 motion-reduce:transition-none min-[961px]:min-h-9"
                                                            disabled={isConfirming}
                                                            onClick={() => void confirmAlert(alert.publicId)}
                                                        >
                                                            <Check size={16} className="!p-0 !text-current" aria-hidden="true"/>
                                                            Confirm
                                                        </PhoneHQButton>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div id={detailsId} hidden={!isExpanded} className="border-t border-solid border-zinc-200 bg-zinc-50 p-4 min-[961px]:px-5">
                                            {isExpanded && (
                                                <>
                                                    <p className="mb-5 whitespace-pre-line break-words text-sm leading-6 text-zinc-700 [overflow-wrap:anywhere]">
                                                        {alert.message}
                                                    </p>
                                                    <dl className="grid min-w-0 gap-4 text-xs min-[600px]:grid-cols-2 min-[961px]:grid-cols-3">
                                                        <div className="min-w-0">
                                                            <dt className="text-zinc-500">Alert ID</dt>
                                                            <dd className="mt-1 break-all font-medium text-zinc-900">{alert.publicId}</dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-zinc-500">Triggered</dt>
                                                            <dd className="mt-1 font-medium text-zinc-900"><time dateTime={alert.triggeredAt}>{formatDateTime(alert.triggeredAt)}</time></dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-zinc-500">Overall alert status</dt>
                                                            <dd className="mt-1"><StatusBadge status={alert.alertStatus} size="md" className="!rounded-full !border-0"/></dd>
                                                        </div>
                                                        {alert.currentLevelSequence != null && (
                                                            <div>
                                                                <dt className="text-zinc-500">Escalation level</dt>
                                                                <dd className="mt-1"><LevelBadge sequenceNo={alert.currentLevelSequence}/></dd>
                                                            </div>
                                                        )}
                                                        {alert.notificationChannel != null && (
                                                            <div>
                                                                <dt className="text-zinc-500">Channel</dt>
                                                                <dd className="mt-1"><ChannelBadge channel={alert.notificationChannel} className="!border-0"/></dd>
                                                            </div>
                                                        )}
                                                        {alert.confirmedAt && (
                                                            <div>
                                                                <dt className="text-zinc-500">Confirmed</dt>
                                                                <dd className="mt-1 font-medium text-zinc-900"><time dateTime={alert.confirmedAt}>{formatDateTime(alert.confirmedAt)}</time></dd>
                                                            </div>
                                                        )}
                                                    </dl>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {!isInitialLoading && !isError && totalCount > 0 && alerts.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-zinc-50 px-4 py-3 min-[961px]:mt-0 min-[961px]:rounded-none min-[961px]:border-t min-[961px]:border-solid min-[961px]:border-zinc-200 min-[961px]:px-5">
                            <p className="text-xs text-zinc-500">
                                Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + alerts.length, totalCount)} of {totalCount}
                            </p>
                            {totalPages > 1 && (
                                <div className="min-w-0 [&>div]:flex-wrap [&_button]:min-h-11 [&_button]:px-2 [&_button]:motion-reduce:transition-none min-[961px]:[&_button]:min-h-9">
                                    <EnsPagination
                                        page={page}
                                        totalPages={totalPages}
                                        isFetching={isFetching}
                                        onChange={changePage}
                                        idPrefix="myAlerts"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default MyAlerts;
